import { Box, IconButton, Typography, Tooltip, LinearProgress } from '@mui/material';
import { Close, Fullscreen, FullscreenExit } from '@mui/icons-material';
import { useState, useCallback, useEffect } from 'react';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { retroColors } from './theme';

/**
 * Full-screen dark layout for the investor demo.
 * No navigation, no auth UI — just cinematic dark chrome.
 */
export default function InvestorLayout({
    children,
    currentStep,
    totalSteps,
    actLabel,
    onClose,
}) {
    const [isFullscreen, setIsFullscreen] = useState(false);

    const toggleFullscreen = useCallback(() => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    }, []);

    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handler);
        return () => document.removeEventListener('fullscreenchange', handler);
    }, []);

    const progress = ((currentStep + 1) / totalSteps) * 100;

    return (
        <Box sx={{
            minHeight: '100vh',
            bgcolor: retroColors.bg,
            color: vortxColors.white,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
        }}>
            {/* Top bar — minimal: logo + act label + controls */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 1.5,
                borderBottom: `1px solid rgba(178, 140, 80, 0.15)`,
                bgcolor: 'rgba(0,0,0,0.3)',
                backdropFilter: 'blur(8px)',
                zIndex: 10,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <img
                        src="/assets/vortx-logo.png"
                        alt="Vortx"
                        style={{ height: '28px' }}
                    />
                    {actLabel && (
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: vortxColors.gold,
                            textTransform: 'uppercase',
                            letterSpacing: '0.15em',
                            borderLeft: `1px solid ${vortxColors.gold}`,
                            pl: 2,
                        }}>
                            {actLabel}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        color: retroColors.muted,
                    }}>
                        {currentStep + 1} / {totalSteps}
                    </Typography>
                    <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                        <IconButton
                            onClick={toggleFullscreen}
                            size="small"
                            sx={{ color: retroColors.muted, '&:hover': { color: vortxColors.gold } }}
                        >
                            {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    {onClose && (
                        <Tooltip title="Exit Demo">
                            <IconButton
                                onClick={onClose}
                                size="small"
                                sx={{ color: retroColors.muted, '&:hover': { color: vortxColors.error } }}
                            >
                                <Close fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Progress bar */}
            <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                    height: 2,
                    bgcolor: 'rgba(178, 140, 80, 0.1)',
                    '& .MuiLinearProgress-bar': {
                        bgcolor: vortxColors.gold,
                        transition: 'transform 0.6s ease',
                    },
                }}
            />

            {/* Main content area */}
            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                {children}
            </Box>
        </Box>
    );
}
